import { useEffect } from "react";

import Button from "../Button/Button";
import styles from "./Modal.module.css";

export interface ModalProps {
  title: string;
  visible: boolean;
  okText?: string;
  closeText?: string;
  bodyText?: string;
  onConfirm?: () => void;
  onClose: () => void;
  onCancel?: () => void;
  hideCancel?: boolean;
  hideConfirm?: boolean;
}

const Modal = ({
  title,
  visible,
  okText = "OK",
  closeText,
  bodyText,
  onConfirm,
  onClose,
  onCancel,
  hideCancel = false,
  hideConfirm = false,
}: ModalProps) => {
  const handleConfirm = () => {
    onConfirm?.();
    onClose();
  };

  const handleCancel = () => {
    if (onCancel != null) {
      onCancel();
    }
    onClose();
  };

  // Close on Escape while the modal is open
  useEffect(() => {
    if (!visible) {
      return;
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [visible, onClose]);

  return visible ? (
    <div
      className={styles.backdrop}
      onClick={(e) => {
        // Only the backdrop itself (not a click bubbling up from inside the
        // dialog) should dismiss.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={styles.modal} role="dialog" aria-modal="true">
        <div className={styles.title}>{title}</div>
        {bodyText && (
          <div className={styles.bodyText}>
            {bodyText.split("\n").map((text, index) => (
              <p key={index}>{text}</p>
            ))}
          </div>
        )}
        <div className={styles.buttons}>
          {!hideCancel && (
            <Button type="reset" onClick={handleCancel} variant="secondary">
              {closeText || "Cancel"}
            </Button>
          )}
          {!hideConfirm && (
            <Button onClick={handleConfirm}>{okText || "OK"}</Button>
          )}
        </div>
      </div>
    </div>
  ) : null;
};

export default Modal;
